/**
 * The formula builder's model — rows of terms — and its two-way mapping onto
 * the server's sum-of-products grammar.
 *
 *   { sum: [ { product: [ {var|const, divide_by?}, ... ] }, ... ] }
 *
 * A ROW is one product; rows are summed. A TERM is one of:
 *   { kind: 'var',    name: 'loa_ft' }            — a builder variable
 *   { kind: 'const',  value: '1.3' }              — the string the user typed
 *   { kind: 'legacy', term: { ...wire term } }     — anything the builder cannot
 *                                                    edit: a divide_by, or a var
 *                                                    outside BUILDER_VARS. Kept
 *                                                    verbatim so an existing
 *                                                    Hull Sides formula survives
 *                                                    a round trip untouched.
 *
 * `marineCalc.js` EVALUATES formulas and `areaFormulaDisplay.js` DESCRIBES
 * them; this module only BUILDS them. It never throws — the UI renders
 * whatever it is given, and `validateRows` reports problems as strings.
 *
 * Only import is `marineCalc.js` (with the `.js` extension Node's ESM resolver
 * needs) — tested directly by `scripts/test/areaFormulaBuilder.test.mjs`.
 */
import { BUILDER_VARS } from './marineCalc.js';

export { BUILDER_VARS };

/** A boat an installer can picture, for the worked example. */
export const SAMPLE_BOAT = { loa_ft: 22, beam_ft: 8, hull_height_in: 30 };

export const emptyRow = () => ({ terms: [] });
export const varTerm = (name) => ({ kind: 'var', name });
export const constTerm = (value) => ({ kind: 'const', value: String(value ?? '') });

const hasOwn = (o, k) => Object.prototype.hasOwnProperty.call(o, k);

function termFromWire(term) {
  if (!term || typeof term !== 'object') return null;
  const legacy = { kind: 'legacy', term };
  if (term.divide_by !== undefined) return legacy;
  if (hasOwn(term, 'const')) {
    const n = Number(term.const);
    return Number.isFinite(n) ? constTerm(n) : legacy;
  }
  if (term.var) {
    return BUILDER_VARS.includes(term.var) ? varTerm(term.var) : legacy;
  }
  return legacy;
}

/** Wire formula → rows. `[]` for anything that is not a sum of products. */
export function rowsFromFormula(formula) {
  if (!formula || !Array.isArray(formula.sum)) return [];
  const rows = [];
  for (const group of formula.sum) {
    if (!group || !Array.isArray(group.product)) return [];
    const terms = [];
    for (const term of group.product) {
      const t = termFromWire(term);
      if (t === null) return [];
      terms.push(t);
    }
    rows.push({ terms });
  }
  return rows;
}

function termToWire(t) {
  if (t.kind === 'var') return { var: t.name };
  if (t.kind === 'const') return { const: Number(t.value) };
  return t.term;
}

/** Rows → wire formula. Rows with no terms are skipped; null when none remain. */
export function formulaFromRows(rows) {
  const groups = (rows || [])
    .filter((r) => r && Array.isArray(r.terms) && r.terms.length > 0)
    .map((r) => ({ product: r.terms.map(termToWire) }));
  return groups.length > 0 ? { sum: groups } : null;
}

/**
 * Can the formula be saved? Strings, not throws: the UI shows `error` under
 * the builder. Every rule here is one the server prompt asks it to enforce
 * too, so a formula that passes cannot be rejected on save for structure.
 */
export function validateRows(rows, allowedVars) {
  const list = (rows || []).filter(Boolean);
  const allowed = allowedVars || [];
  if (list.length === 0) return { ok: false, error: 'Add at least one row to the formula.' };
  let varCount = 0;
  for (let i = 0; i < list.length; i++) {
    const terms = Array.isArray(list[i].terms) ? list[i].terms : [];
    if (terms.length === 0) return { ok: false, error: `Row ${i + 1} has no terms.` };
    for (const t of terms) {
      if (t.kind === 'const') {
        const n = Number(t.value);
        if (t.value === '' || !Number.isFinite(n) || n <= 0) {
          return { ok: false, error: `Row ${i + 1}: every number must be greater than 0.` };
        }
      } else if (t.kind === 'var') {
        if (!allowed.includes(t.name)) {
          return { ok: false, error: `Row ${i + 1} uses a variable that is not selected.` };
        }
        varCount++;
      } else if (t.kind === 'legacy') {
        if (t.term && t.term.var) varCount++;
      } else {
        return { ok: false, error: `Row ${i + 1} has an unrecognised term.` };
      }
    }
  }
  if (varCount === 0) {
    return { ok: false, error: 'A formula must use at least one variable. Use Flat pricing for a fixed price.' };
  }
  return { ok: true, error: null };
}

/** Drop var terms not in `allowedVars`, then rows left with no terms. */
export function stripVars(rows, allowedVars) {
  const allowed = allowedVars || [];
  return (rows || [])
    .filter(Boolean)
    .map((r) => ({ terms: (r.terms || []).filter((t) => t.kind !== 'var' || allowed.includes(t.name)) }))
    .filter((r) => r.terms.length > 0);
}

/** True iff every var the formula references is in `allowedVars`. */
export function formulaUsesOnly(formula, allowedVars) {
  if (!formula || !Array.isArray(formula.sum) || formula.sum.length === 0) return false;
  const allowed = allowedVars || [];
  for (const group of formula.sum) {
    for (const term of (group && group.product) || []) {
      if (term && term.var && !allowed.includes(term.var)) return false;
    }
  }
  return true;
}
